/* eslint-disable react-hooks/rules-of-hooks */

import React from 'react';
import {
    GoogleMap,
    Marker,
    useLoadScript,
    StreetViewPanorama
} from '@react-google-maps/api';
import getConfig from 'next/config';

import {
    ListingInteractiveMapProps,
    ListingInteractiveMapWrapperProps
} from './ListingInteractiveMap.types';
import { MapContainer, StyledError } from './ListingInteractiveMap.styles';

import HOME_ICON from 'public/assets/map/map-dynamic-small-purple-pin-ballon-with-outline.svg';

export const ListingInteractiveMap = ({
    latitude,
    longitude,
    showStreetView,
    isLoaded,
    loadError
}: ListingInteractiveMapProps) => {
    const homeLocationLatLng = {
        lat: latitude,
        lng: longitude
    } as google.maps.LatLngLiteral;

    const mapOptions = {
        streetViewControl: true,
        fullscreenControl: false,
        minZoom: 3,
        gestureHandling: 'greedy'
    } as google.maps.MapOptions;

    const streetViewOptions = {
        position: homeLocationLatLng,
        visible: showStreetView
    } as google.maps.StreetViewPanoramaOptions;

    if (loadError) {
        return (
            <StyledError isRegular size="7" color="text-body">
                We&apos;re having trouble connecting to Google Maps. Please
                check your internet connection and try again.
            </StyledError>
        );
    }

    const renderMap = () => (
        <MapContainer data-testid="listing-map-container">
            <GoogleMap
                center={homeLocationLatLng}
                zoom={15}
                options={mapOptions}
            >
                {showStreetView && (
                    <StreetViewPanorama options={streetViewOptions} />
                )}
                <Marker
                    position={homeLocationLatLng}
                    icon={HOME_ICON}
                    title="Home Pin"
                />
            </GoogleMap>
        </MapContainer>
    );

    return isLoaded ? renderMap() : <span>Loading...</span>;
};

function ListingInteractiveMapWrapper({
    data,
    showStreetView = false
}: ListingInteractiveMapWrapperProps) {
    if (!data?.location?.coordinates) {
        return null;
    }

    const googleMapsClientId =
        process.env.NEXT_PUBLIC_MAPS_CLIENT_ID ||
        getConfig().publicRuntimeConfig.MAPS_CLIENT_ID;

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: '',
        googleMapsClientId,
        channel: 'Lex-LDP'
    });

    const { latitude, longitude } = data.location.coordinates;

    return (
        <ListingInteractiveMap
            latitude={latitude ?? 0}
            longitude={longitude ?? 0}
            showStreetView={showStreetView}
            isLoaded={isLoaded}
            loadError={loadError}
        />
    );
}

export default ListingInteractiveMapWrapper;
